import { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon, EyeSlashIcon, XMarkIcon, StarIcon } from '@heroicons/react/24/outline';
import { StarIcon as StarSolidIcon } from '@heroicons/react/24/solid';
import { planService, Plan, CreatePlanData, FeatureFlags } from '../../../services/plan.service';
import { toast } from 'react-hot-toast';

export default function PlanList({ hideHeader = false }: { hideHeader?: boolean }) {
  const defaultFeatureFlags: FeatureFlags = {
    eSign: false,
    payroll: false,
    biometricImport: false,
    advancedReporting: false,
    apiAccess: false,
    multiCurrency: false,
    customBranding: false,
    prioritySupport: false,
    clientPortal: false,
    timeTracking: false,
    projectManagement: false,
    documentManagement: false,
  };
  const [plans, setPlans] = useState<Plan[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<'all' | 'active'>('all');
  const [showModal, setShowModal] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState<CreatePlanData>({
    name: '',
    displayName: '',
    description: '',
    cost: 0,
    currency: 'CAD',
    duration: 'monthly',
    maxUsers: 5,
    maxClients: 10,
    features: [],
    sequenceNumber: 1,
    featureFlags: defaultFeatureFlags,
  });

  useEffect(() => {
    loadPlans();
  }, [filter]);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const response = await planService.getAllPlans(filter === 'active');
      setPlans(response.data);
    } catch (error: any) {
      toast.error(error.message || 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleStatus = async (planId: string) => {
    try {
      await planService.togglePlanStatus(planId);
      toast.success('Plan status updated successfully');
      loadPlans();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update plan status');
    }
  };

  const handleToggleRecommended = async (planId: string) => {
    try {
      await planService.togglePlanRecommended(planId);
      toast.success('Plan recommendation status updated successfully');
      loadPlans();
    } catch (error: any) {
      toast.error(error.message || 'Failed to update recommendation status');
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm('Are you sure you want to delete this plan?')) return;

    try {
      await planService.deletePlan(planId);
      toast.success('Plan deleted successfully');
      loadPlans();
    } catch (error: any) {
      toast.error(error.message || 'Failed to delete plan');
    }
  };

  const openCreateModal = () => {
    setEditingPlan(null);
    setFormData({
      name: '',
      displayName: '',
      description: '',
      cost: 0,
      currency: 'CAD',
      duration: 'monthly',
      maxUsers: 5,
      maxClients: 10,
      features: [],
      sequenceNumber: plans.length + 1,
      featureFlags: defaultFeatureFlags,
    });
    setShowModal(true);
  };

  const openEditModal = async (plan: Plan) => {
    setEditingPlan(plan);
    setFormData({
      name: plan.name,
      displayName: plan.displayName,
      description: plan.description,
      cost: plan.cost,
      currency: plan.currency,
      duration: plan.duration,
      maxUsers: plan.maxUsers,
      maxClients: plan.maxClients,
      features: plan.features,
      sequenceNumber: plan.sequenceNumber,
      featureFlags: { ...defaultFeatureFlags, ...plan.featureFlags },
    });
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPlan(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      if (editingPlan) {
        await planService.updatePlan(editingPlan._id, formData);
        toast.success('Plan updated successfully');
      } else {
        await planService.createPlan(formData);
        toast.success('Plan created successfully');
      }
      closeModal();
      loadPlans();
    } catch (error: any) {
      toast.error(error.message || `Failed to ${editingPlan ? 'update' : 'create'} plan`);
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: currency,
    }).format(amount);
  };

  const getDurationLabel = (duration: string) => {
    const labels: Record<string, string> = {
      monthly: 'Per Month',
      quarterly: 'Per Quarter',
      yearly: 'Per Year',
      lifetime: 'Lifetime',
    };
    return labels[duration] || duration;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!hideHeader && (
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Plan Management</h1>
            <p className="mt-1 text-sm text-gray-500">
              Manage subscription plans and pricing
            </p>
          </div>
          <button
            onClick={openCreateModal}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Create Plan
          </button>
        </div>
      )}
      {hideHeader && (
        <button
          onClick={openCreateModal}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          Create Plan
        </button>
      )}

      {/* Filters */}
      <div className="flex space-x-2">
        <button
          onClick={() => setFilter('all')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            filter === 'all'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          All Plans
        </button>
        <button
          onClick={() => setFilter('active')}
          className={`px-4 py-2 text-sm font-medium rounded-md ${
            filter === 'active'
              ? 'bg-blue-100 text-blue-700'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          Active Only
        </button>
      </div>

      {/* Plans Grid */}
      {plans.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <p className="text-gray-500">No plans found</p>
          <button
            onClick={openCreateModal}
            className="mt-4 text-blue-600 hover:text-blue-700"
          >
            Create your first plan
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {plans.map((plan) => (
            <div key={plan._id} className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow">
              {/* Plan Header */}
              <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {plan.displayName}
                      </h3>
                      {plan.isRecommended && (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                          <StarSolidIcon className="h-3 w-3 mr-1" />
                          Recommended
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-gray-500">{plan.description}</p>
                  </div>
                  <span
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      plan.isActive
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {plan.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>

                {/* Pricing */}
                <div className="mt-4">
                  <div className="flex items-baseline">
                    <span className="text-3xl font-bold text-gray-900">
                      {formatCurrency(plan.cost, plan.currency)}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      {getDurationLabel(plan.duration)}
                    </span>
                  </div>
                </div>

                {/* Key Features */}
                <div className="mt-4 space-y-2">
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium">Users:</span>
                    <span className="ml-2">{plan.maxUsers}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium">Clients:</span>
                    <span className="ml-2">{plan.maxClients}</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-600">
                    <span className="font-medium">Sequence:</span>
                    <span className="ml-2">#{plan.sequenceNumber}</span>
                  </div>
                </div>

                {/* Feature Flags */}
                <div className="mt-4 flex flex-wrap gap-1">
                  {plan.featureFlags.eSign && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                      E-Sign
                    </span>
                  )}
                  {plan.featureFlags.clientPortal && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800">
                      Client Portal
                    </span>
                  )}
                  {plan.featureFlags.timeTracking && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                      Time Tracking
                    </span>
                  )}
                  {plan.featureFlags.projectManagement && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-indigo-100 text-indigo-800">
                      Project Management
                    </span>
                  )}
                  {plan.featureFlags.documentManagement && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-yellow-100 text-yellow-800">
                      Document Management
                    </span>
                  )}
                  {plan.featureFlags.advancedReporting && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-pink-100 text-pink-800">
                      Advanced Reporting
                    </span>
                  )}
                  {plan.featureFlags.apiAccess && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-800">
                      API Access
                    </span>
                  )}
                  {plan.featureFlags.customBranding && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                      Custom Branding
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4">
                  <div className="flex space-x-2">
                    <button
                      onClick={() => openEditModal(plan)}
                      className="text-blue-600 hover:text-blue-700"
                      title="Edit"
                    >
                      <PencilIcon className="h-5 w-5" />
                    </button>
                    <button
                      onClick={() => handleToggleStatus(plan._id)}
                      className={`${
                        plan.isActive
                          ? 'text-gray-600 hover:text-gray-700'
                          : 'text-green-600 hover:text-green-700'
                      }`}
                      title={plan.isActive ? 'Deactivate' : 'Activate'}
                    >
                      {plan.isActive ? (
                        <EyeSlashIcon className="h-5 w-5" />
                      ) : (
                        <EyeIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleToggleRecommended(plan._id)}
                      className={`${
                        plan.isRecommended
                          ? 'text-yellow-600 hover:text-yellow-700'
                          : 'text-gray-400 hover:text-yellow-600'
                      }`}
                      title={plan.isRecommended ? 'Unmark as Recommended' : 'Mark as Recommended'}
                    >
                      {plan.isRecommended ? (
                        <StarSolidIcon className="h-5 w-5" />
                      ) : (
                        <StarIcon className="h-5 w-5" />
                      )}
                    </button>
                    <button
                      onClick={() => handleDelete(plan._id)}
                      className="text-red-600 hover:text-red-700"
                      title="Delete"
                    >
                      <TrashIcon className="h-5 w-5" />
                    </button>
                  </div>
                  {plan.stripePriceId && (
                    <span className="text-xs text-green-600">
                      ✓ Synced
                    </span>
                  )}
                </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal for Create/Edit Plan */}
      {showModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 mx-auto p-5 border w-full max-w-4xl shadow-lg rounded-md bg-white mb-10">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-2xl font-bold text-gray-900">
                {editingPlan ? 'Edit Plan' : 'Create New Plan'}
              </h3>
              <button
                onClick={closeModal}
                className="text-gray-400 hover:text-gray-500"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-semibold mb-4">Basic Information</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Plan Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="e.g., starter"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Display Name *</label>
                    <input
                      type="text"
                      required
                      value={formData.displayName}
                      onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
                      placeholder="e.g., Starter Plan"
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    />
                  </div>
                </div>
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700">Description *</label>
                  <textarea
                    required
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Describe the plan..."
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                  />
                </div>
              </div>

              {/* Pricing */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-semibold mb-4">Pricing</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Cost *</label>
                    <input
                      type="number"
                      required
                      min="0"
                      step="0.01"
                      value={formData.cost}
                      onChange={(e) => setFormData({ ...formData, cost: parseFloat(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Currency *</label>
                    <select
                      value={formData.currency}
                      onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    >
                      <option value="CAD">CAD</option>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Duration *</label>
                    <select
                      value={formData.duration}
                      onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    >
                      <option value="monthly">Monthly</option>
                      <option value="quarterly">Quarterly</option>
                      <option value="yearly">Yearly</option>
                      <option value="lifetime">Lifetime</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Limits */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-semibold mb-4">Limits</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Users *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.maxUsers}
                      onChange={(e) => setFormData({ ...formData, maxUsers: parseInt(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Max Clients *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.maxClients}
                      onChange={(e) => setFormData({ ...formData, maxClients: parseInt(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Sequence Number *</label>
                    <input
                      type="number"
                      required
                      min="1"
                      value={formData.sequenceNumber}
                      onChange={(e) => setFormData({ ...formData, sequenceNumber: parseInt(e.target.value) })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 px-3 py-2 border"
                    />
                  </div>
                </div>
              </div>

              {/* Feature Flags */}
              <div className="border-b pb-6">
                <h4 className="text-lg font-semibold mb-4">Feature Flags</h4>
                <div className="grid grid-cols-2 gap-4">
                  {Object.entries(formData.featureFlags).map(([key, value]) => (
                    <label key={key} className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={value}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            featureFlags: { ...formData.featureFlags, [key]: e.target.checked },
                          })
                        }
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                      <span className="text-sm text-gray-700 capitalize">
                        {key.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Actions */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Saving...' : editingPlan ? 'Update Plan' : 'Create Plan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
